import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker } from '@shopify/polaris';

const CALENDAR_PORTAL_Z_INDEX = 10050;

function parseIsoDate(value) {
  if (!value) return null;
  const raw = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const dt = new Date(`${raw}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function calendarIconStyle() {
  return { flexShrink: 0, pointerEvents: 'none' };
}

export function CalendarGlyph() {
  return (
    <svg style={calendarIconStyle()} width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="4" width="14" height="14" rx="2" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="7" y1="2" x2="7" y2="6" />
      <line x1="13" y1="2" x2="13" y2="6" />
    </svg>
  );
}

/**
 * Inline ISO date picker — Polaris calendar in a body portal (works above full-screen modals).
 */
export default function IsoDateCalendarPicker({
  value = '',
  onChange,
  disabled = false,
  busy = false,
  activatorStyle,
  activatorLabel,
  title = 'Pick a date',
  weekStartsOn = 1,
  onOpenChange,
  darkMode = false,
  // Optional floor for selectable days (Polaris disables everything BEFORE it).
  // Off by default — the performance panel picks past dates on purpose.
  disableDatesBefore
}) {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const selectedDate = useMemo(() => parseIsoDate(value) || new Date(), [value]);
  const [month, setMonth] = useState(selectedDate.getMonth());
  const [year, setYear] = useState(selectedDate.getFullYear());

  useEffect(() => {
    setMonth(selectedDate.getMonth());
    setYear(selectedDate.getFullYear());
  }, [value, selectedDate]);

  const setPanelOpen = useCallback((next) => {
    if (disabled || busy) return;
    setOpen(next);
    onOpenChange?.(next);
  }, [busy, disabled, onOpenChange]);

  const reposition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = 320;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - panelWidth - 8
    );
    setCoords({
      top: rect.bottom + 6,
      left
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    reposition();
    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setPanelOpen(false);
    };
    const onScroll = () => setPanelOpen(false);
    // Capture phase + stopPropagation: Escape closes the calendar only, and
    // does not also close the modal or card editor hosting it.
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setPanelOpen(false);
      anchorRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition, setPanelOpen]);

  const handleDateChange = useCallback(({ start }) => {
    if (!start || disabled || busy) return;
    const iso = toIsoDate(start);
    if (!iso) return;
    setMonth(start.getMonth());
    setYear(start.getFullYear());
    onChange?.(iso);
    setPanelOpen(false);
  }, [busy, disabled, onChange, setPanelOpen]);

  const toggleOpen = () => {
    if (disabled || busy) return;
    setPanelOpen(!open);
  };

  const panel = open && coords ? createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={title}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: CALENDAR_PORTAL_Z_INDEX,
        width: 320,
        maxWidth: 'calc(100vw - 16px)',
        padding: 12,
        borderRadius: 12,
        border: darkMode ? '1px solid rgba(148, 163, 184, 0.35)' : '1px solid #e2e8f0',
        background: darkMode ? '#1f2937' : '#ffffff',
        boxShadow: darkMode ? '0 12px 32px rgba(0,0,0,0.45)' : '0 12px 32px rgba(15, 23, 42, 0.18)'
      }}
    >
      <DatePicker
        month={month}
        year={year}
        selected={{ start: selectedDate, end: selectedDate }}
        onChange={handleDateChange}
        onMonthChange={(nextMonth, nextYear) => {
          setMonth(nextMonth);
          setYear(nextYear);
        }}
        weekStartsOn={weekStartsOn}
        disableDatesBefore={disableDatesBefore}
      />
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled || busy}
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={title}
        style={{
          ...activatorStyle,
          cursor: disabled || busy ? 'default' : 'pointer',
          opacity: busy ? 0.7 : 1,
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        <CalendarGlyph />
        <span style={{ pointerEvents: 'none' }}>{activatorLabel}</span>
      </button>
      {panel}
    </>
  );
}

export {
  parseIsoDate,
  toIsoDate,
  CALENDAR_PORTAL_Z_INDEX
};
